var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Suspense, forwardRef, lazy, useEffect, useImperativeHandle, useMemo, } from 'react';
import { ErrorBoundary } from '@servicetitan/error-boundary';
import { Log } from '@servicetitan/log-service';
import { useOptionalDependencies } from '@servicetitan/react-ioc';
import { EXPOSED_DEPENDENCIES_TOKEN, useCancelation, } from './common';
import { Delayed } from './delayed';
import { EVENT_BUS_TOKEN, useEventBus } from './event-bus';
import { EXPOSED_DEPENDENCIES } from './globals';
import { TtlCache, getBundleInfo, validateBundleInfo, loadScripts, webComponent, } from './utils';
export const Loader = forwardRef((props, ref) => {
    var _a;
    const { cache = true, basename, fallbackSrc, onRequestError, retries, src } = props;
    const { loadingFallback, loadingFallbackDelayed = true } = props;
    const bus = useEventBus();
    const [logService, exposedDependencies = EXPOSED_DEPENDENCIES, eventBus = bus] = useOptionalDependencies(Log, EXPOSED_DEPENDENCIES_TOKEN, EVENT_BUS_TOKEN);
    const cancelation = useCancelation();
    const LoadingFallback = useMemo(() => getFallback(loadingFallback, 'Loading...', { delayed: loadingFallbackDelayed }), [loadingFallback, loadingFallbackDelayed]);
    const ErrorFallback = useMemo(() => getFallback(props.errorFallback, 'Something went wrong'), [props.errorFallback]);
    const Loader = useMemo(() => lazyLoader({
        basename,
        cache,
        cancelation,
        exposedDependencies,
        eventBus,
        fallbackSrc,
        logService,
        onRequestError,
        retries,
        src,
    }), [
        basename,
        cache,
        cancelation,
        exposedDependencies,
        eventBus,
        logService,
        onRequestError,
        fallbackSrc,
        retries,
        src,
    ]);
    useImperativeHandle(ref, () => eventBus, [eventBus]);
    return (_jsx(ErrorBoundary, { moduleName: src, fallback: ErrorFallback, children: _jsx(Suspense, { fallback: _jsx(LoadingFallback, {}), children: _jsx(Loader, { data: (_a = props.data) !== null && _a !== void 0 ? _a : {}, className: props.className, LoadingFallback: LoadingFallback }) }) }));
});
function lazyLoader(props) {
    const { cancelation } = props;
    const LazyWebComponent = lazyWebComponent(props);
    return (props) => {
        useEffect(() => {
            return () => {
                cancelation.abort();
            };
        }, []);
        return (_jsx(LazyWebComponent, { data: props.data, className: props.className, LoadingFallback: props.LoadingFallback }));
    };
}
function lazyWebComponent(props) {
    const { basename, cache, cancelation, exposedDependencies, eventBus, fallbackSrc, logService, onRequestError, retries, src, } = props;
    return lazy(() => __awaiter(this, void 0, void 0, function* () {
        let bundleInfo = cache ? bundleCache.get(src) : undefined;
        if (!bundleInfo) {
            try {
                bundleInfo = yield getBundleInfo(src, fallbackSrc, exposedDependencies, cancelation.signal, retries, onRequestError);
            }
            catch (e) {
                if (cancelation.signal.aborted) {
                    return {
                        default: () => null,
                    };
                }
                throw e;
            }
            if (cache) {
                bundleCache.set(src, bundleInfo, typeof cache === 'number' ? cache : undefined);
            }
            validateBundleInfo(bundleInfo, { exposedDependencies, logService, packageName: src });
            yield loadScripts(bundleInfo);
        }
        return {
            default: webComponent({
                basename,
                bundleInfo,
                eventBus,
                exposedDependencies,
                logService,
            }),
        };
    }));
}
function getFallback(node, message, { delayed = false } = {}) {
    const fallbackNode = node !== undefined ? node : _jsx("h2", { children: message });
    return delayed ? () => _jsx(Delayed, { children: fallbackNode }) : () => fallbackNode;
}
const MINUTE = 1000 * 60;
const bundleCache = new TtlCache({ ttlMs: 15 * MINUTE });
//# sourceMappingURL=loader.js.map